import { z } from "zod";

import { LoadingSpinner } from "../../../../../components/ui/loadingSpinner/loadingSpinner";
import { formatRelativeTime } from "../../../../../lib/utils";
import { ColumnActionLogWithUserSchema } from "../../../../../models/audit/columnActionLog";

export type ActivityLog = z.infer<typeof ColumnActionLogWithUserSchema>;

interface ActivityLogListProps {
  activityLogs: ActivityLog[];
  logsLoading: boolean;
}

/** The activity feed rows: an avatar, user, relative time, and the log message,
 * joined by a vertical timeline rule. Loading and empty states included so both
 * the overview section and the trail-panel popover can render it directly. */
export function ActivityLogList({
  activityLogs,
  logsLoading,
}: ActivityLogListProps) {
  if (logsLoading) {
    return (
      <div className="flex justify-center py-4">
        <LoadingSpinner />
      </div>
    );
  }

  if (activityLogs.length === 0) {
    return <div className="py-2 text-xs text-stone-500">No activity yet.</div>;
  }

  return (
    <div className="space-y-3">
      {activityLogs.map((log, index) => (
        <div
          key={log.id}
          className="flex gap-3 text-xs text-font-secondary relative"
        >
          {index < activityLogs.length - 1 && (
            <div className="absolute left-[11px] top-6 bottom-0 w-px bg-stone-200" />
          )}
          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-stone-200 flex items-center justify-center text-[10px] font-medium text-stone-600 relative z-10">
            {log.user?.email?.charAt(0).toUpperCase() || "?"}
          </div>
          <div className="flex-1 min-w-0 pt-0.5">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-medium text-stone-900">
                {log.user?.email || "Unknown user"}
              </span>
              <span className="text-stone-500">
                {formatRelativeTime(log.createdAt)}
              </span>
            </div>
            <div className="mt-1 text-stone-600">{log.log}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
